import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchaseOrder = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [formData, setFormData] = useState({
    po_number: `PO-${Date.now()}`,
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    supplier_id: '',
    supplier_name: '',
    notes: '',
    created_by: 'Admin',
    tax_amount: 0,
    status: 'Pending'
  });
  const [selectedItems, setSelectedItems] = useState([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
    fetchOrders();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await apiClient.get('/api/inventory/items');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setItems(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/inventory/suppliers');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setSuppliers(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/api/inventory/purchase-orders');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setOrders(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({ ...formData, supplier_id: supplierId, supplier_name: supplier.name });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...selectedItems];
    updated[index][field] = value;
    if (field === 'item_id') {
      const item = items.find(i => i.id === value);
      if (item) {
        updated[index].item_name = item.item_name;
        updated[index].unit_price = item.unit_price;
        updated[index].total = updated[index].quantity * item.unit_price;
      }
    } else if (field === 'quantity' || field === 'unit_price') {
      updated[index].total = updated[index].quantity * updated[index].unit_price;
    }
    setSelectedItems(updated);
  };

  const calculateTotal = () => selectedItems.reduce((sum, item) => sum + item.total, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const validItems = selectedItems.filter(item => item.item_id && item.quantity > 0);
    if (validItems.length === 0) {
      alert('Please add at least one item');
      setLoading(false);
      return;
    }
    const totalAmount = calculateTotal();
    const grandTotal = totalAmount + formData.tax_amount;
    try {
      const response = await apiClient.post('/api/inventory/purchase-orders', { ...formData, items: validItems, total_amount: totalAmount, grand_total: grandTotal });
      if (response.status === 200 || response.data) {
        alert('Purchase order created successfully!');
        fetchOrders();
        setFormData({
          po_number: `PO-${Date.now()}`,
          order_date: new Date().toISOString().split('T')[0],
          expected_delivery_date: '',
          supplier_id: '',
          supplier_name: '',
          notes: '',
          created_by: 'Admin',
          tax_amount: 0,
          status: 'Pending'
        });
        setSelectedItems([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create PO');
      }
    } catch (error) {
      alert('Failed to create PO');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create Purchase Order</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>PO Number</Label>
                <Input value={formData.po_number} disabled />
              </div>
              <div>
                <Label>Order Date *</Label>
                <Input type="date" required value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input type="date" value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Supplier *</Label>
              <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Items</h3>
                <Button type="button" size="sm" onClick={() => setSelectedItems([...selectedItems, { item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }])}>
                  <Plus className="w-4 h-4 mr-1" />Add Item
                </Button>
              </div>
              {selectedItems.map((item, index) => (
                <div key={index} className="grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label>Item</Label>
                    <Select value={item.item_id} onValueChange={(value) => handleItemChange(index, 'item_id', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((i) => (
                          <SelectItem key={i.id} value={i.id}>{i.item_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))} />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))} />
                  </div>
                  <div>
                    <Label>Total</Label>
                    <Input value={item.total} disabled />
                  </div>
                  <div>
                    <Button type="button" size="sm" variant="destructive" onClick={() => setSelectedItems(selectedItems.filter((_, i) => i !== index))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="space-y-2 pt-3 border-t">
                <div className="flex justify-between"><span>Subtotal:</span><span>₹{calculateTotal()}</span></div>
                <div className="flex justify-between items-center">
                  <span>Tax Amount:</span>
                  <Input type="number" step="0.01" min="0" value={formData.tax_amount} onChange={(e) => setFormData({ ...formData, tax_amount: parseFloat(e.target.value) })} className="w-32" />
                </div>
                <div className="flex justify-between text-lg font-bold"><span>Grand Total:</span><span>₹{calculateTotal() + formData.tax_amount}</span></div>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes" />
            </div>
            <Button type="submit" disabled={loading} className="w-full">{loading ? 'Creating...' : 'Create Purchase Order'}</Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Purchase Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">PO #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Supplier</th>
                  <th className="text-left p-3">Total</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan="6" className="text-center p-4 text-gray-500">No orders found.</td></tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{order.po_number}</td>
                      <td className="p-3">{formatDate(order.order_date)}</td>
                      <td className="p-3">{order.supplier_name}</td>
                      <td className="p-3">₹{order.grand_total}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${order.status === 'Received' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <Button size="sm" variant="outline" onClick={() => setViewOrder(order)}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {viewOrder && (
        <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Purchase Order Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><strong>PO Number:</strong> {viewOrder.po_number}</div>
                <div><strong>Date:</strong> {formatDate(viewOrder.order_date)}</div>
                <div><strong>Supplier:</strong> {viewOrder.supplier_name}</div>
                <div><strong>Status:</strong> {viewOrder.status}</div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Items:</h4>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b"><th className="text-left p-2">Item</th><th className="text-left p-2">Qty</th><th className="text-left p-2">Price</th><th className="text-left p-2">Total</th></tr>
                  </thead>
                  <tbody>
                    {viewOrder.items.map((item, idx) => (
                      <tr key={idx} className="border-b"><td className="p-2">{item.item_name}</td><td className="p-2">{item.quantity}</td><td className="p-2">₹{item.unit_price}</td><td className="p-2">₹{item.total}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right font-bold">Grand Total: ₹{viewOrder.grand_total}</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PurchaseOrder;